import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCustomerList,  searchCustomer } from "src/apis/customerApi";
import { AddressForm } from "src/components/common/form/address/AddressForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { AddressJusoDto } from "src/dtos/address/addressApiResponseDto";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import useUiuxStore from "src/store/useUiuxStore";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

import { BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { handleKeyDown, syntheticEvent } from "src/utils/commonForm/commonFormUtils";
import { defaultSAL2020_R01HeaderDto, SAL2020_R01DetailDto, SAL2020_R01HeaderDto } from "./Dto_SAL2020_R01";
import { useSAL2020_R01Store } from "./Store_SAL2020_R01";
import { getEmployeeList } from "src/apis/employeeApi";
import { formatDate } from "src/utils/string/stringUtil";



interface Props {
	editable: boolean
	defaultSAL2020_R01HeaderRow: SAL2020_R01HeaderDto;
}

export const Info_SAL2020_R01 = (props: Props) => {

	const {
			editable,
			defaultSAL2020_R01HeaderRow
		} = props;

	// 
	const { vatTypeList, moneyCdList, compCdList } = useCommonCodeStore();
	const { setCodeSearchModalProps, setAddressModalProps } = useUiuxStore();
	const { SAL2020_R01DetailList, deletedSAL2020_R01DetailList,
			selectedSAL2020_R01,
			editSAL2020_R01, setEditSAL2020_R01,
	} = useSAL2020_R01Store();


	const custInfoHandleFoucsId = 'infoCustInput';
	const empInfoHandleFoucsId = 'infoEmpInput';
	const addressHandleFoucsId = 'addressInput2';

	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: custInfoHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
				post: 'post',
				address: 'address',
				detailAddress: 'detailAddress',
				telNo: 'telNo',
				faxNo: 'faxNo',
				moneyCd: 'moneyCd',
				taxEmail: 'email',
			},
			searchFunction: async (searchText: string) => {
				
				const customerResponse = await searchCustomer(searchText);

				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: editSAL2020_R01?.customCd || '',
				customNm: editSAL2020_R01?.customNm || '',
			}),
		},
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: empInfoHandleFoucsId,
			responseMapping: {
				empNo: 'empNo',
				empNm: 'empNm'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			},
			extraFilter: () => ({
				empNo: editSAL2020_R01?.empNo || '',
				empNm: editSAL2020_R01?.empNm || '',
			}),
		}
		
	];

	// Info 폼 이벤트 훅 사용
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editSAL2020_R01,
		setEditData: setEditSAL2020_R01,
		defaultData: defaultSAL2020_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'SAL2020_R01'
	});

	// 출고일자 달력 팝업 상태
	const [deliveryPickerVisible, setDeliveryPickerVisible] = useState(false);
	const [pickerPos, setPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 매출일자 달력 팝업 상태
	const [salesPickerVisible, setSalesPickerVisible] = useState(false);
	const [salesPickerPos, setSalesPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 부가세 입력 포커스 상태 및 표시값 관리
	const [vatAmtInputFocused, setVatAmtInputFocused] = useState(false);
	const [vatAmtInputValue, setVatAmtInputValue] = useState('');
	const [vatAmtManuallyEdited, setVatAmtManuallyEdited] = useState(false);

	// 계산된 금액들을 메모이제이션
	const calculatedAmounts = useMemo(() => {
		const newSalAmt = SAL2020_R01DetailList.reduce((sum, item) => 
			sum + (deletedSAL2020_R01DetailList.includes(item.itemCd) ? 0 : item.itemAmt), 0);
		
		// 조회 시에는 DB 값 그대로 사용, 편집 시에만 자동 계산 적용
		let newVatAmt = editSAL2020_R01?.vatAmt || 0;
		
		// 부가세 구분이 일반과세인지 확인 (코드명에 '일반' 또는 '과세'가 포함된 경우)
		const currentVatType = vatTypeList.find(vat => vat.minorCd === editSAL2020_R01?.vatType);
		const isGeneralTax = currentVatType?.codeNm?.includes('일반') || currentVatType?.codeNm?.includes('과세') || editSAL2020_R01?.vatType === '10';
		
		// 편집 모드이고 사용자가 부가세를 수동으로 수정하지 않은 경우에만 자동 계산
		if (isGeneralTax && !vatAmtManuallyEdited && SAL2020_R01DetailList.some(item => item.inserted || item.isModified)) {
			newVatAmt = Math.round(newSalAmt * 0.1); // 매출액의 10% (반올림)
		} else if (vatAmtManuallyEdited) {
			// 수동 편집된 경우 기존 부가세 값 유지
			newVatAmt = editSAL2020_R01?.vatAmt || 0;
		} else {
			// 조회 시에는 기존 부가세 값 유지 (0이 아닌 경우)
			newVatAmt = editSAL2020_R01?.vatAmt || 0;
		}
		
		// 공급총액 정확히 계산
		const newTotalAmt = newSalAmt + newVatAmt;
		
		return { newSalAmt, newVatAmt, newTotalAmt };
	}, [SAL2020_R01DetailList, deletedSAL2020_R01DetailList, editSAL2020_R01?.vatAmt, editSAL2020_R01?.vatType, vatTypeList, vatAmtManuallyEdited]);

	// 계산된 금액을 editSAL2020_R01에 적용
	useEffect(() => {
		if (calculatedAmounts) {
			setEditSAL2020_R01({
				...defaultSAL2020_R01HeaderRow,
				...editSAL2020_R01,
				salesAmt: calculatedAmounts.newSalAmt,
				vatAmt: calculatedAmounts.newVatAmt,
				totalAmt: calculatedAmounts.newTotalAmt
			});
		}
	}, [calculatedAmounts]);

	useEffect(() => {
		// editSAL2020_R01.vatAmt가 바뀌면 표시값도 갱신 (포커스 없을 때만)
		if (!vatAmtInputFocused) {
			setVatAmtInputValue(Number(editSAL2020_R01?.vatAmt || 0).toLocaleString());
		}
	}, [editSAL2020_R01?.vatAmt, vatAmtInputFocused]);

	// 조회 시 수동 편집 상태 초기화
	useEffect(() => {
		// 조회된 데이터가 있고 편집되지 않은 상태라면 수동 편집 상태 리셋
		if (editSAL2020_R01?.salBillNo && !SAL2020_R01DetailList.some(item => item.inserted || item.isModified)) {
			setVatAmtManuallyEdited(false);
		}

	}, [editSAL2020_R01?.salBillNo, SAL2020_R01DetailList]);

	const handleDateChange = useCallback((field: keyof SAL2020_R01HeaderDto, value: any) => {
		setEditSAL2020_R01({
			...defaultSAL2020_R01HeaderRow,
			...editSAL2020_R01,
			[field]: value
		});
	}, [editSAL2020_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		// 매출액 또는 부가세가 변경될 때 공급총액도 같이 변경
		if (name === 'salAmt' || name === 'vatAmt') {
			const salAmt = name === 'salAmt' ? Number(value) : Number(editSAL2020_R01?.salesAmt ?? 0);
			const vatAmt = name === 'vatAmt' ? Number(value) : Number(editSAL2020_R01?.vatAmt ?? 0);
			
			// 부가세를 수동으로 수정한 경우
			if (name === 'vatAmt') {
				setVatAmtManuallyEdited(true); // 수동 편집 상태로 설정
			}
			
			setEditSAL2020_R01({
				...defaultSAL2020_R01HeaderRow,
				...editSAL2020_R01,
				[name]: Number(value), // 숫자로 변환하여 저장
				totalAmt: salAmt + vatAmt
			});
			return;
		}

		// 부가세 구분이 변경될 때 자동 계산
		if (name === 'vatType') {
			let newVatAmt = editSAL2020_R01?.vatAmt || 0;
			
			// 부가세 구분이 일반과세인지 확인
			const selectedVatType = vatTypeList.find(vat => vat.minorCd === value);
			const isGeneralTax = selectedVatType?.codeNm?.includes('일반') || selectedVatType?.codeNm?.includes('과세') || value === '10';
			
			// 부가세 구분이 변경되면 수동 편집 상태를 리셋하고 자동 계산
			if (isGeneralTax) {
				newVatAmt = Number(editSAL2020_R01?.salesAmt || 0) * 0.1; // 반올림 없음
				setVatAmtManuallyEdited(false); // 부가세 구분 변경 시 수동 편집 상태 리셋
			}
			
			setEditSAL2020_R01({
				...defaultSAL2020_R01HeaderRow,
				...editSAL2020_R01,
				[name]: value,
				vatAmt: newVatAmt,
				totalAmt: Number(editSAL2020_R01?.salesAmt || 0) + newVatAmt
			});
			return;
		}

		setEditSAL2020_R01({
			...defaultSAL2020_R01HeaderRow,
			...editSAL2020_R01,
			[name]: value
		});
	}, [editSAL2020_R01]);

	const handleAddressSearchClick = () => {
		setAddressModalProps({
			onSelect: (data: AddressJusoDto) => {
				setEditSAL2020_R01({
					...defaultSAL2020_R01HeaderRow,
					...editSAL2020_R01,
					post: data.zipNo,
					address: data.roadAddr,
				})
				setAddressModalProps(undefined);
				handleKeyDown(syntheticEvent(addressHandleFoucsId), 'focus');
			},
			handleFoucsId: addressHandleFoucsId // 주소 검색 후 입력 포커스 이동을 위한 아이디
		})
	}

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300">
				{/* editSAL2020_R01 : {JSON.stringify(editSAL2020_R01)} <br /> */}
				<InputForm title="전표No" value={editSAL2020_R01?.salBillNo || ""} 
					editable={false}
					name="salBillNo" 
					onChange={handleChange} />
				<DropDownForm
					title="사업장"
					name="compCd"
					initLabelLess={true}
					value={editSAL2020_R01?.compCd}
					data={compCdList}
					editable={true}
					onChange={handleChange}
				/>
					<BRDiv />

				<SingleDateForm
					title="매출일자"
					value={editSAL2020_R01?.salesDt ? new Date(editSAL2020_R01.salesDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('salesDt', formatDate(value))}
					editable={true}
					visible={salesPickerVisible}				setVisible={setSalesPickerVisible}
					onChange={handleChange}
					pickerPos={salesPickerPos}						setPickerPos={setSalesPickerPos}
				/>
				<CodeForm
					label="매출담당자"
					value={editSAL2020_R01?.empNo || ""}
					custNmValue={editSAL2020_R01?.empNm || ""}
					editable={true}
					name="empNo"
					handleChange={getFieldHandlers('empNo').onChange}
					onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
					onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
				/>
					<BRDiv />

				<CodeForm
					label="거래처"
					value={editSAL2020_R01?.customCd || ""}
					custNmValue={editSAL2020_R01?.customNm || ""}
					editable={true}
					handleFoucsId={custInfoHandleFoucsId}
					name="customCd"
					handleChange={getFieldHandlers('customCd').onChange}
					onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
					onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
				/>
				<DropDownForm
					title="기준화폐"
					value={editSAL2020_R01?.moneyCd}
					name="moneyCd"
					initLabelLess={true}
					data={moneyCdList}
					editable={true}
					onChange={handleChange}
				/>
					<BRDiv />
				
				<InputForm	title="전화번호" 
						value={editSAL2020_R01?.telNo || ""} 
						editable={true} 
						name="telNo" 
						onChange={handleChange}
					/>
				<InputForm	title="팩스번호" 
					value={editSAL2020_R01?.faxNo || ""} 
					editable={true} 
					name="faxNo" 
					onChange={handleChange}
				/>
				<BRDiv />

				<DropDownForm
						title="부가세구분" 
						value={editSAL2020_R01?.vatType}
						name="vatType"
						initLabelLess={true}
						data={vatTypeList}
						editable={true}
						onChange={handleChange} />
					<BRDiv />

				<AddressForm
					post={editSAL2020_R01?.post || ""}
					address={editSAL2020_R01?.address || ""}
					detailAddress={editSAL2020_R01?.detailAddress || ""}
					editable={true}
					handleFoucsId={addressHandleFoucsId}
					onChange={handleChange}
					onSearchClick={handleAddressSearchClick}
				/>
					<BRDiv />
				<InputForm	title="매출액" 
							value={editSAL2020_R01?.salesAmt || 0} 
							editable={false} 
							name="salesAmt" 
							onChange={handleChange} 
							/>

				<SingleDateForm
					title="출고일자"
					value={editSAL2020_R01?.deliveryDt ? new Date(editSAL2020_R01.deliveryDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('deliveryDt', formatDate(value))}
					editable={true}
					visible={deliveryPickerVisible}				setVisible={setDeliveryPickerVisible}
					onChange={handleChange}
					pickerPos={pickerPos}						setPickerPos={setPickerPos}
				/>
				<BRDiv /> {/* ← 이 줄 추가! */}
				<InputForm	title="부가세" 
							value={editSAL2020_R01?.vatAmt ?? 0} 
							editable={true} 
							name="vatAmt" 
							onChange={handleChange}
						/>
				<InputForm	title="공급총액" 
							value={editSAL2020_R01?.totalAmt ?? 0} 
							editable={false} 
							name="totalAmt" 
							onChange={handleChange} 
							/>
							<BRDiv />
				<InputForm
					title="비고"
					value={editSAL2020_R01?.remark || ""}
					editable={true}
					name="remark"
					wrapperOption={{
						wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[10px]",
						titleStyle: "w-[100px] min-w-[100px] text-left",
						inputStyle: "w-full min-w-full"
					}}
					onChange={handleChange}
				/>
		</div>
	)
}